import AnimatedSection from "@/components/AnimatedSection";

function FloatingParticle({ className, delay = 0 }: { className: string; delay?: number }) {
  return (
    <div
      className={`absolute w-1 h-1 rounded-full bg-accent-400/50 ${className}`}
      style={{ animation: `pulse-dot 3s ease-in-out ${delay}s infinite` }}
    />
  );
}

interface PageCTAProps {
  heading: string;
  text?: string;
  primaryCTA: string;
  secondaryCTA?: string;
}

export default function PageCTA({ heading, text, primaryCTA, secondaryCTA }: PageCTAProps) {
  return (
    <section className="py-20 md:py-28 bg-cta-gradient relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-400/15 to-transparent" />
        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-400/20 to-transparent" />
        <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-400/15 to-transparent" />
      </div>

      <FloatingParticle className="top-[20%] left-[15%]" delay={0} />
      <FloatingParticle className="top-[30%] left-[70%]" delay={1} />
      <FloatingParticle className="top-[60%] left-[25%]" delay={2} />
      <FloatingParticle className="top-[45%] left-[85%]" delay={0.5} />
      <FloatingParticle className="top-[75%] left-[50%]" delay={1.5} />
      <FloatingParticle className="top-[15%] left-[45%]" delay={2.5} />
      <FloatingParticle className="top-[80%] left-[10%]" delay={0.8} />
      <FloatingParticle className="top-[55%] left-[60%]" delay={1.8} />

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-accent-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary-500/5 rounded-full blur-3xl" />

      <div className="container-astro relative z-10 text-center">
        <AnimatedSection>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
            {heading}
          </h2>
        </AnimatedSection>

        {text && (
          <AnimatedSection delay={150}>
            <p className="text-base md:text-lg text-background-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              {text}
            </p>
          </AnimatedSection>
        )}

        <AnimatedSection delay={300}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-base font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
            >
              {primaryCTA}
              <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            {secondaryCTA && (
              <a
                href="/hire-laravel-developer"
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group"
              >
                {secondaryCTA}
                <i className="ri-chat-1-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            )}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}